import { Component, OnInit, Renderer2, ViewEncapsulation } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { nameof } from 'ts-simple-nameof';
import { DialogDataService } from '../../../../../dialog/dialog-data.service';
import { DialogService } from '../../../../../dialog/dialog.service';
import { QuestionDefinition, QuestionValueChange, SelectInputType } from '../../../../../forms/form.types';
import { GenericFormGroupComponent } from '../../../../../forms/generic-form-group.component';
import { GradingRule } from '../../../../../shared/interface/grading-rule.interface';
import { AlertService } from '../../../../../shared/services/alert.service';
import { ReflectionService } from '../../../../../shared/utilities/reflection.service';
import { GradingRuleService } from '../grading-rule.service';

@Component({
    selector: 'app-grading-rule-edit',
    templateUrl: './grading-rule-edit.component.html',
    styleUrls: ['./grading-rule-edit.component.scss'],
    encapsulation: ViewEncapsulation.Emulated
})
export class GradingRuleEditComponent extends GenericFormGroupComponent implements OnInit {
    get showGradeMode(): boolean {
        return this.controls && this.reflection.isNotNullOrEmpty(this.controls.gradeLevel.value);
    }

    get showValidGrade(): boolean {
        return (
            this.controls &&
            this.controls.id.value > 0 &&
            this.reflection.isNotNullOrEmpty(this.controls.gradeLevel.value) &&
            this.reflection.isNotNullOrEmpty(this.controls.gradeMode.value)
        );
    }

    get showValidGrades(): boolean {
        return (
            this.controls &&
            this.controls.id.value === -1 &&
            this.reflection.isNotNullOrEmpty(this.controls.gradeLevel.value) &&
            this.reflection.isNotNullOrEmpty(this.controls.gradeMode.value)
        );
    }

    controls: {
        id: FormControl<number>;
        gradeLevel: FormControl<string>;
        gradeMode: FormControl<string>;
        validGrade?: FormControl<string>;
        validGradeModes?: FormControl<Array<string>>;
        isEnabled: FormControl<boolean>;
    };

    questions: {
        gradeLevel: QuestionDefinition;
        gradeMode: QuestionDefinition;
        validGrade: QuestionDefinition;
        validGradeModes: QuestionDefinition;
        isEnabled: QuestionDefinition;
    };

    constructor(
        private dataService: DialogDataService<GradingRule>,
        private dialogService: DialogService<GradingRule>,
        private service: GradingRuleService,
        private reflection: ReflectionService,
        private alert: AlertService,
        protected renderer2: Renderer2
    ) {
        super(renderer2);
        this.controls = {
            id: new FormControl<number>(this.dataService.data.id, Validators.required),
            gradeLevel: new FormControl<string>(this.dataService.data.gradeLevel, Validators.required),
            gradeMode: new FormControl<string>(this.dataService.data.gradeMode, Validators.required),
            isEnabled: new FormControl<boolean>(this.dataService.data.isEnabled ?? false)
        };
        if (this.dataService.data.id === -1) {
            this.controls.validGradeModes = new FormControl<Array<string>>([], Validators.required);
        } else {
            this.controls.validGrade = new FormControl<string>(this.dataService.data.validGrade, Validators.required);
        }
        this.createFormGroup(this.controls);
        this.questions = {
            gradeLevel: {
                input: {
                    discriminator: 'SELECT',
                    property: nameof<GradingRule>((m) => m.gradeLevel),
                    control: this.controls.gradeLevel,
                    required: true,
                    options$: this.service.selectListPost({
                        what: 'gradeLevel'
                    })
                }
            },
            gradeMode: {
                input: {
                    discriminator: 'SELECT',
                    property: nameof<GradingRule>((m) => m.gradeMode),
                    control: this.controls.gradeMode,
                    required: true,
                    optionsCallBack: {
                        this: this,
                        function: () => {
                            return this.service.selectListPost({
                                what: 'gradeMode',
                                id: this.controls.id.value,
                                gradeLevel: this.controls.gradeLevel.value
                            });
                        }
                    }
                }
            },
            validGrade: {
                input: {
                    discriminator: 'SELECT',
                    property: nameof<GradingRule>((m) => m.validGrade),
                    control: this.controls.validGrade,
                    required: true,
                    optionsCallBack: {
                        this: this,
                        function: () => {
                            return this.service.selectListPost({
                                what: 'validGrade',
                                id: this.controls.id.value,
                                gradeLevel: this.controls.gradeLevel.value,
                                gradeMode: this.controls.gradeMode.value
                            });
                        }
                    }
                }
            },
            validGradeModes: {
                input: {
                    discriminator: 'SELECT',
                    property: 'validGradeModes',
                    control: this.controls.validGradeModes,
                    isMultiple: true,
                    required: true,
                    optionsCallBack: {
                        this: this,
                        function: () => {
                            return this.service.selectListPost({
                                what: 'validGrade',
                                id: this.controls.id.value,
                                gradeLevel: this.controls.gradeLevel.value,
                                gradeMode: this.controls.gradeMode.value
                            });
                        }
                    }
                }
            },
            isEnabled: {
                input: {
                    discriminator: 'CHECKBOX',
                    property: nameof<GradingRule>((m) => m.isEnabled),
                    control: this.controls.isEnabled
                }
            }
        };
    }

    ngOnInit(): void {}

    onSave() {
        this.setSaving();
        if (this.controls.id.value === -1) {
            this.service
                .createManyFromOnePromise(this.getFormValue(), 'Grading Rule(s) created!')
                .then((m) => {
                    this.setDoneSaving();
                    m.forEach((v) => {
                        this.dialogService.send(this.dataService, v);
                    });
                })
                .catch(() => {
                    this.setDoneSaving();
                });
        } else {
            this.service
                .savePromise(this.getFormValue(), 'Grading Rule saved!')
                .then((v) => {
                    this.setDoneSaving();
                    this.dialogService.send(this.dataService, v);
                    this.controls.id.setValue(v.id);
                })
                .catch(() => {
                    this.setDoneSaving();
                });
        }
    }

    onGradeLevelChange($event: QuestionValueChange) {
        this.controls.gradeMode.setValue(null);
        if (this.controls.validGrade) {
            this.controls.validGrade.setValue(null);
        } else if (this.controls.validGradeModes) {
            this.controls.validGradeModes.setValue([]);
        }
        if ($event.payload) {
            (this.questions.gradeMode.input as SelectInputType).updateOptions = true;
        }
    }

    onGradeModeChange($event: QuestionValueChange) {
        if (this.controls.validGrade) {
            this.controls.validGrade.setValue(null);
        } else if (this.controls.validGradeModes) {
            this.controls.validGradeModes.setValue([]);
        }
        if ($event.payload) {
            if (this.controls.validGrade) {
                (this.questions.validGrade.input as SelectInputType).updateOptions = true;
            } else if (this.controls.validGradeModes) {
                (this.questions.validGradeModes.input as SelectInputType).updateOptions = true;
            }
        } else {
        }
    }
}
